import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActionIcon, Alert, Anchor, Box, Grid, Group, Loader, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconArrowLeft, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { EventForm } from 'components/events/EventForm/EventFormMain'
import type { EventFormDeskMode, EventFormFieldId, EventFormValues } from 'components/events/EventForm/types'
import { useQueryDeskCandidate } from 'hooks/desk'
import { useDeskDecision } from 'hooks/desk/useDeskDecision'
import { useDeskTranslation } from 'hooks/desk/useDeskTranslation'
import { useDeskRecheck } from 'hooks/desk/useDeskRecheck'
import { useCustomVenueHistory } from 'hooks/desk/useCustomVenueHistory'
import { useQueryVenueAliases, VENUE_ALIASES_QUERY_KEY } from 'hooks/desk/useQueryVenueAliases'
import { useQueryCities } from 'hooks/cities'
import { useQueryEventsByApplication, useQueryTags } from 'hooks/events'
import { useQueryOrganizers } from 'hooks/organizers'
import { useQueryVenues } from 'hooks/venues'
import { useAppTranslation } from 'hooks/useTranslation'
import { queryVenues } from 'supabase/venues'
import {
  ApplicationRow,
  DESK_RULES_MATCH_BY,
  queryDeskCandidate,
  queryDeskNextCandidate,
  queryDeskTabOf,
  queryDeskTargetEvent
} from 'supabase/desk'
import { insertVenueAlias } from 'supabase/desk/insertVenueAlias'
import { flagsByField, recheckLine, withRecheckChecks, withRestoreChecks } from './checkFieldMap'
import { ChecksPanel, DESK_STATUS_BANNER_ID } from './ChecksPanel'
import { withFormValues } from './compareRows'
import type { DeskReturnState } from './Desk'
import { DeskFieldFlag } from './DeskFieldFlag'
import { mapDeskError } from './deskErrors'
import { publicImageUrl } from './deskImages'
import {
  buildDeskPrefill,
  deskWeekly,
  DeskPrefillDeps,
  END_TIME_PLACEHOLDER,
  listedVenuesNamed,
  listedVenuesResembling,
  RESEMBLING_VENUES_MAX,
  resolveDeskVenue,
  START_TIME_PLACEHOLDER,
  timeNotStated,
  venueKeyRows,
  venueNameKey
} from './deskPrefill'
import { SourceBadge } from './DeskTable'
import { DESK_TAB_LABEL_KEYS, DESK_TABS, DeskTab } from './deskTabs'
import { buildEditorBaseline, computeEditedFields, toSubmitted } from './editedFields'
import { ImageStrip } from './ImageStrip'
import { isUpdateProposal, MatchPanel, showsMatchPanel } from './MatchPanel'
import { RejectMenu, RejectNoteHost, showDeskError } from './RejectMenu'
import { SourcePostPanel } from './SourcePostPanel'
import { UpdateProposalPanel } from './UpdateProposalPanel'
import { useDeskTownChange } from './useDeskTownChange'
import { isSaveAsVenueEligible, mergeVenueHistory, pickDeskLocation, toVenueSuggestion } from './venueHistory'
import { buildVenueIndex, venueIdsForText } from './venueNameKey'
import type { VenueAliasRow } from './venueNameKey'
import { rememberAliasOffer, RememberAliasOffer, showSuggestedVenueHint } from './venueTeach'

/**
 * Router state is history, not a database row: anything that can push history
 * can write it. Only a tab of the closed four and a positive integer page are
 * read off it; anything else is dropped (the deskTabs.ts rule for the URL).
 */
const readReturnState = (state: unknown): Partial<DeskReturnState> => {
  const raw = (state ?? {}) as { tab?: unknown; page?: unknown }
  const tab = DESK_TABS.find((allowed) => allowed === raw.tab)
  const page = typeof raw.page === 'number' && Number.isInteger(raw.page) && raw.page >= 1 ? raw.page : undefined
  return { tab, page }
}

const readId = (value: string | undefined): number | null =>
  value && /^\d+$/.test(value) ? Number(value) : null

// Twin of ApplicationEdit.readErrorCode: the hook's error is typed `Error`,
// and nothing guarantees a `code` member.
const readErrorCode = (error: unknown): string | null => {
  const code = (error as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : null
}

/**
 * The town a custom-venue history lookup is keyed on (33.3 WR-03): the listed
 * city's name when there is a city_id, else the typed text. The initial lookup
 * and the town re-run both use it, so the same town always sends the same
 * text (and shares one cache entry).
 */
const townOf = (
  cityId: number | null,
  text: string | null | undefined,
  cities: { id: number; name: string }[] | undefined
): { cityId: number | null; cityText: string } => ({
  cityId,
  cityText: (cityId !== null ? cities?.find((city) => city.id === cityId)?.name : undefined) || text || ''
})

const listHref = (tab: DeskTab, page?: number) =>
  page && page > 1 ? `/desk?tab=${tab}&page=${page}` : `/desk?tab=${tab}`

const DECISION_KEYS: Record<string, string> = {
  approved: 'desk.decided.approved',
  rejected: 'desk.decided.rejected',
  attached: 'desk.decided.attached',
  update_applied: 'desk.decided.update_applied',
  update_dismissed: 'desk.decided.update_dismissed'
}

/**
 * The candidate screen at `/desk/:id` (D-05..D-09, DESK-02, DESK-07, IMG-04,
 * UI-SPEC §D).
 *
 * Left: what the editor flagged (first, since it is why the candidate is
 * here), then the source post with its image strip.
 * Right: the status banner and the existing EventForm in desk approve mode,
 * pre-filled from the editor's resolved ids (D-06), each section wrapped with
 * its check flag (D-07).
 *
 * Approve is the real convertApplication (D-05). Its processed write is the
 * guarded `desk_approve`, which records the fields she edited, measured
 * against a pure transform of the row (D-09), never a snapshot of the live
 * form. The candidate is re-read just before any event is created (Pitfall 9).
 *
 * EventForm's two application venue/organizer info props, which feed its AI
 * matchers, are never passed, and `disableAiMatch` is set: both matchers would
 * reach the paid PROD `openai` function (Pitfall 5, T-33-57). A grep gate and
 * DeskCandidate.test.tsx hold this.
 *
 * After any decision the screen opens the next candidate of the same tab, or
 * returns to the tab when there is none (UI-SPEC U-03).
 *
 * The duplicate side-by-side (MatchPanel, DESK-03) sits above the form when
 * the editor suggested a duplicate. An update proposal (desk_queue's Updates
 * rule, isUpdateProposal) gets UpdateProposalPanel in place of the convert
 * form and the status banner, and no Reject menu (D-15 defines two actions);
 * it moves on through Update proposals.
 *
 * A custom venue's location (spec-desk-venue-location D-03, D-03b, D-07,
 * D-10): the candidate town's custom-venue history is read before the form
 * mounts, so the prefill and the DESK-07 baseline see the same pin (a listed
 * venue, no venue name or no town never waits). A town set or changed in the
 * form re-runs the lookup and offers the location through `locationFill`;
 * with no pin the map centres on the town; a spread of pins or a failed
 * lookup is a note under Location. Save as venue (D-05, D-06) is offered from
 * the same history. The town watcher is the shared `useDeskTownChange`, the
 * one Phase 33.1 reuses for its duplicate re-check.
 *
 * The same watcher drives the 33.1 duplicate re-check (useDeskRecheck: D-05,
 * D-28, D-30), whose result is drawn on the flag row in "What the editor
 * flagged" or, for a found duplicate, by the existing Match panel. Approve is
 * never held by it (D-08); the Match panel's actions are held while it runs,
 * and after a failed re-check on a desk-relabelled row (D-10, D-32).
 */
export const DeskCandidate = () => {
  const { id: idParam } = useParams()
  const id = readId(idParam)
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t, language } = useAppTranslation()
  const customVenueKeyword = t('venues.custom')

  const returnState = readReturnState(location.state)
  const page = returnState.page ?? 1

  const { isLoading, isError, error, candidate, rawItem, siblings } = useQueryDeskCandidate(id)
  const { venues, isError: venuesFailed } = useQueryVenues({})
  const { organizers, isError: organizersFailed } = useQueryOrganizers({ page: 1, perPage: 1000 })
  const { tags, isError: tagsFailed } = useQueryTags()
  const { cities, isError: citiesFailed } = useQueryCities()
  // AD-IN-03: a failed lookup is a load error, not "Loading…" forever.
  const lookupFailed = venuesFailed || organizersFailed || tagsFailed || citiesFailed
  // 33.4 D-08: the venue aliases, for the venue lookup and the Remember offer.
  // P-12: a failed read never joins lookupFailed; the desk keeps name-only
  // matching and the Remember offer hides.
  const { aliases, failed: aliasesFailed, settled: aliasesSettled } = useQueryVenueAliases()
  const { approve } = useDeskDecision()

  // A deep link carries no router state: ask desk_queue which tab the
  // candidate sits in. Once known it sticks, so a later re-read after a
  // decision (when it is off the desk) does not flip "Back to" to Ready.
  const tabOf = useQuery({
    queryKey: ['desk', 'tabOf', id],
    queryFn: () => queryDeskTabOf(id as number),
    enabled: id !== null && returnState.tab === undefined
  })
  const [stickyTab, setStickyTab] = useState<DeskTab | null>(null)
  useEffect(() => {
    if (tabOf.data && stickyTab === null) setStickyTab(tabOf.data)
  }, [tabOf.data, stickyTab])
  const tab: DeskTab = returnState.tab ?? stickyTab ?? tabOf.data ?? 'ready'
  const tabName = t(DESK_TAB_LABEL_KEYS[tab])
  const backLabel = t('desk.back_to', { tab: tabName })
  const backHref = listHref(tab, page)

  // The image she swapped to, for this candidate only: a swap on one
  // candidate must never reach the next candidate's form.
  const [swapped, setSwapped] = useState<{ id: number; url: string } | null>(null)

  const decision = candidate?.processed ? candidate.desk_decision ?? 'approved' : null
  const { events: createdEvents } = useQueryEventsByApplication(candidate?.id ?? 0, decision === 'approved')
  const targetEventId = decision === 'attached' ? candidate?.match_target_event_id ?? null : null
  const targetApplicationId =
    decision === 'attached' && targetEventId === null ? candidate?.match_target_application_id ?? null : null
  const targetEvent = useQuery({
    queryKey: ['desk', 'targetEvent', targetEventId],
    queryFn: () => queryDeskTargetEvent(targetEventId as number),
    enabled: targetEventId !== null
  })
  const { events: targetApplicationEvents } = useQueryEventsByApplication(
    targetApplicationId ?? 0,
    targetApplicationId !== null
  )

  // spec-desk-venue-location (33.3): the custom-venue history of the
  // candidate's town (`initialHistory`, feeds the prefill and the baseline,
  // fixed per candidate) and of the form's town after she changes it
  // (`currentHistory`, D-03b). A failed lookup never joins `lookupFailed`
  // (D-10): the desk keeps working with no history.
  const inApproveMode = Boolean(candidate) && !candidate?.processed && !(candidate && isUpdateProposal(candidate))
  const initialTown = useMemo(
    () => townOf(candidate?.city_id ?? null, candidate?.venue_village, cities),
    [candidate?.city_id, candidate?.venue_village, cities]
  )
  const onCustomVenue =
    inApproveMode &&
    !!candidate &&
    !!venues &&
    !resolveDeskVenue(candidate, venues, {}, aliases) &&
    venueNameKey(candidate.event_venue_location) !== ''
  const initialHistory = useCustomVenueHistory(initialTown, onCustomVenue)
  const townWatch = useDeskTownChange(candidate?.id)
  // WR-03: built like initialTown. Picking a listed city empties venue_city
  // in the form, and an empty text would skip the RPC's text branch (events
  // with no city_id) and miss the initial lookup's cache entry.
  const currentTown = townWatch.watched
    ? townOf(townWatch.watched.cityId, townWatch.watched.cityText, cities)
    : initialTown
  const currentHistory = useCustomVenueHistory(currentTown, onCustomVenue && townWatch.townChanged)
  const activeHistory = townWatch.townChanged ? currentHistory : initialHistory
  // WR-02: the venue name as the form has it now (reported on mount and on
  // every change), for this candidate only. The history rows hold every
  // custom name of the town, so a renamed venue is matched with no new
  // request. Before the first report it is the candidate's own name.
  const [typedVenue, setTypedVenue] = useState<{ id: number; name: string } | null>(null)
  const candidateId = candidate?.id
  const onVenueNameChange = useCallback(
    (name: string) => {
      if (candidateId === undefined) return
      setTypedVenue((prev) => (prev && prev.id === candidateId && prev.name === name ? prev : { id: candidateId, name }))
    },
    [candidateId]
  )
  const venueNameNow =
    typedVenue && typedVenue.id === candidateId ? typedVenue.name : candidate?.event_venue_location
  // 33.4: the venue the form has selected now, by the id the form resolved
  // (never by name), for this candidate only. null before the first report,
  // on the custom venue, an empty field or an unknown name.
  const [selectedVenue, setSelectedVenue] = useState<{ candidateId: number; name: string; id: number | null } | null>(
    null
  )
  const onVenueChange = useCallback(
    (venue: { name: string; id: number | null }) => {
      if (candidateId === undefined) return
      setSelectedVenue((prev) =>
        prev && prev.candidateId === candidateId && prev.name === venue.name && prev.id === venue.id
          ? prev
          : { candidateId, name: venue.name, id: venue.id }
      )
    },
    [candidateId]
  )
  const selectedVenueId = selectedVenue && selectedVenue.candidateId === candidateId ? selectedVenue.id : null
  const selectedVenueRow =
    selectedVenueId === null ? null : venues?.find((venue) => venue.id === selectedVenueId) ?? null
  // D-15: the Remember offer for the current selection; null hides the box.
  // aliases null = the read failed (P-12), which gives no offer.
  // The candidate's town as the form has it now (the editor's city_id until
  // she changes the town): the selected venue must be in it (code review,
  // D-15 town defect, desk walk W2).
  const formTownCityId = townWatch.watched ? townWatch.watched.cityId : candidate?.city_id ?? null
  const rememberOffer = useMemo(
    () =>
      candidate && venues
        ? rememberAliasOffer({
            application: candidate,
            selectedVenue: selectedVenueRow,
            venues,
            aliases: aliasesFailed || !aliasesSettled ? null : aliases,
            townCityId: formTownCityId
          })
        : null,
    [candidate, venues, selectedVenueRow, aliases, aliasesFailed, aliasesSettled, formTownCityId]
  )
  // D-14: the box is default off and ticked for one offer only. The key holds
  // the candidate, venue and text, so a changed or cleared venue (or the next
  // candidate) unchecks it, and coming back to the venue shows it unchecked.
  const rememberKey =
    rememberOffer && candidateId !== undefined ? `${candidateId}|${rememberOffer.venueId}|${rememberOffer.text}` : null
  const [rememberCheckedKey, setRememberCheckedKey] = useState<string | null>(null)
  useEffect(() => {
    if (rememberCheckedKey !== null && rememberCheckedKey !== rememberKey) setRememberCheckedKey(null)
  }, [rememberKey, rememberCheckedKey])
  const rememberChecked = rememberKey !== null && rememberCheckedKey === rememberKey
  // D-16: read by onApproved at write time, so the alias written is the offer
  // still on screen and ticked when the approve succeeded.
  const rememberWriteRef = useRef<RememberAliasOffer | null>(null)
  rememberWriteRef.current = rememberChecked ? rememberOffer : null
  // P-11: the layer-A index (names and aliases) for the Save-as-venue guard.
  const venueIndex = useMemo(() => buildVenueIndex(venueKeyRows(venues ?? []), aliases), [venues, aliases])
  // Read by the town re-run below, so a rename alone never refills Location
  // (only a town change does, D-03b), but a town change after a rename uses
  // the new name.
  const venueNameRef = useRef(venueNameNow)
  useEffect(() => {
    venueNameRef.current = venueNameNow
  }, [venueNameNow])
  // P1: the form reads its initial values once, so it waits for the history.
  // Sticky per candidate: once the form could mount, it is never hidden again.
  const historyReadyRef = useRef<number | null>(null)
  const historyReady =
    !onCustomVenue || initialHistory.settled || (!!candidate && historyReadyRef.current === candidate.id)
  // D-03b: a town change re-runs the D-03 order on the new town; location
  // only (the address stays as opened). On failure only google_maps can fill.
  const candidateGoogleMaps = candidate?.google_maps
  const townVersion = townWatch.townVersion
  const locationFill = useMemo(
    () =>
      onCustomVenue && townVersion > 0 && currentHistory.settled
        ? {
            venue_location:
              pickDeskLocation(
                candidateGoogleMaps,
                currentHistory.failed ? null : mergeVenueHistory(currentHistory.rows, venueNameRef.current)
              ).location ?? ''
          }
        : null,
    [
      candidateGoogleMaps,
      onCustomVenue,
      townVersion,
      currentHistory.settled,
      currentHistory.failed,
      currentHistory.rows
    ]
  )

  const prefillDeps: DeskPrefillDeps | null = useMemo(
    () =>
      venues && tags && cities && historyReady && aliasesSettled
        ? {
            venues,
            tags,
            cities,
            venueHistory: initialHistory.rows,
            // D-08: the form opens on the venue the shared matcher finds,
            // aliases included; it waits for the read (the form reads its
            // initial values once). A failed read leaves name-only matching.
            venueAliases: aliases,
            publicUrl: publicImageUrl,
            rawItem: {
              image_path: rawItem?.image_path ?? null,
              extra_image_paths: rawItem?.extra_image_paths ?? []
            }
          }
        : null,
    [venues, tags, cities, rawItem, initialHistory.rows, historyReady, aliases, aliasesSettled]
  )
  if (prefillDeps && candidate) historyReadyRef.current = candidate.id
  const prefill = useMemo(
    () => (candidate && prefillDeps ? buildDeskPrefill(candidate, prefillDeps) : undefined),
    [candidate, prefillDeps]
  )
  // spec-desk-auto-translate: fill the empty language side of the pre-fill
  // once per candidate per session. Approve mode only (D-02): a decided
  // candidate shows no form and an update proposal edits an event that is
  // already translated. Called before the early returns (rules of hooks).
  const translation = useDeskTranslation(
    candidate?.id,
    prefill,
    Boolean(candidate) && !candidate?.processed && !(candidate && isUpdateProposal(candidate))
  )

  // `nextTab` is the tab the next candidate comes from: an update proposal
  // always moves on through Update proposals, whatever tab it was opened from.
  const goToNext = async (excludeId: number, nextTab: DeskTab = tab) => {
    let next: number | null = null
    try {
      next = await queryDeskNextCandidate({ tab: nextTab, excludeId })
    } catch {
      next = null
    }
    if (next !== null) {
      const state: DeskReturnState = { tab: nextTab, page: nextTab === tab ? page : 1 }
      navigate(`/desk/${next}`, { state })
      return
    }
    navigate(listHref(nextTab))
  }

  const showAlreadyDecided = () =>
    notifications.show({
      color: 'red',
      title: t('desk.error.already_decided_title'),
      message: t('desk.error.already_decided_body')
    })

  // 33.1: the duplicate re-check rides the one town watcher above (D-28).
  // Called before the early returns (rules of hooks); the hook keeps the
  // options in a ref, so a fresh callback each render is fine.
  const recheck = useDeskRecheck(candidate, townWatch, { onAlreadyDecided: showAlreadyDecided })

  const shell = (content: ReactNode) => (
    <>
      <Stack gap={0} style={{ padding: 24 }}>
        <Anchor component={Link} to={backHref} size='sm' aria-label={backLabel}>
          <Group gap={4} component='span'>
            <IconArrowLeft size={16} />
            {backLabel}
          </Group>
        </Anchor>
        {content}
      </Stack>
      <RejectNoteHost />
    </>
  )

  if (id === null) {
    return shell(
      <Alert mt={16} color='red' icon={<IconAlertTriangle size={18} />} title={t('desk.error.not_found')} />
    )
  }

  if (isLoading) {
    return shell(<Text mt={16}>{t('common.loading')}</Text>)
  }

  // The candidate read is `.single()`, which throws PGRST116 on zero rows (a
  // deleted or never-existing id). Only the code picks the copy; the raw error
  // text is never rendered.
  if (isError || !candidate) {
    const notFound = readErrorCode(error) === 'PGRST116' || (!isError && !candidate)
    return shell(
      <Alert
        mt={16}
        color='red'
        icon={<IconAlertTriangle size={18} />}
        title={t(notFound ? 'desk.error.not_found' : 'desk.error.load')}
      />
    )
  }

  const title = candidate.event_title?.trim() ? candidate.event_title.trim() : t('desk.untitled')
  const source = rawItem?.source ?? candidate.source
  const ordered = siblings ?? []
  const position = ordered.findIndex((sibling) => sibling.id === candidate.id)
  const showSiblings = ordered.length >= 2 && position >= 0
  const openSibling = (siblingId: number) => {
    const state: DeskReturnState = { tab, page }
    navigate(`/desk/${siblingId}`, { state })
  }

  const topBar = (withActions: boolean) => (
    <Group justify='space-between' align='center' mt={8} mb={32} wrap='wrap' gap={16}>
      <Group gap={8} align='center' wrap='wrap'>
        <Text size='xl' fw={600} lh={1.3}>
          {title}
        </Text>
        <SourceBadge source={source} beat={rawItem?.beat ?? null} />
        {showSiblings && (
          <Group gap={4} align='center'>
            <Text size='xs' lh={1.4} c='dimmed'>
              {t('desk.candidate.siblings', { index: position + 1, total: ordered.length })}
            </Text>
            <ActionIcon
              variant='subtle'
              color='gray'
              aria-label={t('desk.candidate.prev_sibling')}
              disabled={position === 0}
              onClick={() => openSibling(ordered[position - 1].id)}
            >
              <IconChevronLeft size={16} />
            </ActionIcon>
            <ActionIcon
              variant='subtle'
              color='gray'
              aria-label={t('desk.candidate.next_sibling')}
              disabled={position === ordered.length - 1}
              onClick={() => openSibling(ordered[position + 1].id)}
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </Group>
        )}
      </Group>
      {withActions && tab !== 'updates' && !isUpdateProposal(candidate) && (
        <RejectMenu
          id={candidate.id}
          title={candidate.event_title}
          variant='button'
          onRejected={() => goToNext(candidate.id)}
        />
      )}
    </Group>
  )

  // A decided candidate renders NO form and no actions (ApplicationEdit's
  // processed arm): editing a decided row would do nothing.
  if (candidate.processed) {
    const decisionKey = decision ? DECISION_KEYS[decision] : undefined
    const decisionLabel = decisionKey ? t(decisionKey) : decision ?? ''
    const createdLinks = decision === 'approved' ? createdEvents.filter((event) => !!event.uid) : []
    const attachedUid =
      decision === 'attached'
        ? targetEvent.data?.uid ?? targetApplicationEvents.find((event) => !!event.uid)?.uid ?? null
        : null
    return shell(
      <>
        {topBar(false)}
        <Alert color='gray' title={t('desk.decided.title', { decision: decisionLabel })}>
          <Stack gap={4}>
            {createdLinks.map((event) => (
              <Anchor key={event.id} component={Link} to={`/Events/${event.uid}`} size='sm'>
                {t('desk.decided.open_event')}
              </Anchor>
            ))}
            {attachedUid && (
              <Anchor component={Link} to={`/Events/${attachedUid}`} size='sm'>
                {t('desk.decided.open_attached_event')}
              </Anchor>
            )}
          </Stack>
        </Alert>
      </>
    )
  }

  if (!prefill || !prefillDeps || !organizers || !tags || !venues) {
    if (lookupFailed) {
      return shell(
        <Alert mt={16} color='red' icon={<IconAlertTriangle size={18} />} title={t('desk.error.load')} />
      )
    }
    return shell(<Text mt={16}>{t('common.loading')}</Text>)
  }

  // D-18: a desk_rules row with a result reads from its label, not from the
  // match_unchecked the re-check keeps so Needs a look has a check.
  const checkRow = withRecheckChecks(withRestoreChecks(candidate))
  const flags = flagsByField(checkRow)
  const line = recheckLine(candidate, recheck)
  // D-10 / D-32: the target may be for an earlier town while a re-check runs,
  // or after one failed on a desk-relabelled row. Approve is unaffected (D-08).
  const holdMatch = recheck.pending || (recheck.failure !== null && candidate.match_by === 'desk_rules')
  const hasStatusChange = candidate.route_checks.includes('status_change')
  // A stated date with no stated time opens on a placeholder time: flag it.
  const missingTime = timeNotStated(candidate)
  const notesFor = (field: EventFormFieldId): string[] =>
    (field === 'start' || field === 'end') && missingTime[field] ? ['desk.field_note.time_not_stated'] : []
  // Code review WR-03: a desk re-check matched on the form's town and dates
  // (D-04), so the Match panel's left column shows those, not the saved row.
  const matchFormRow =
    candidate.match_by === DESK_RULES_MATCH_BY && townWatch.watched
      ? withFormValues(candidate, townWatch.watched, {
          start: missingTime.start ? START_TIME_PLACEHOLDER : null,
          end: missingTime.end ? END_TIME_PLACEHOLDER : null
        })
      : null

  // spec-desk-venue-location: the note under Location (D-04 spread, D-10
  // failure), the Save-as-venue offer (D-05, custom path only) and the map
  // centre on the form's current town when there is no pin (D-07).
  const activeMatch =
    onCustomVenue && !activeHistory.failed
      ? mergeVenueHistory(activeHistory.rows, venueNameNow)
      : null
  const activePick = pickDeskLocation(candidate.google_maps, activeMatch)
  const venueLocationNote = !onCustomVenue
    ? null
    : activeHistory.failed
      ? t('desk.venue_location.history_failed')
      : activePick.source === 'history' && activePick.places >= 2
        ? t('desk.venue_location.different_places', { count: activePick.places })
        : null
  const currentCityId = townWatch.watched ? townWatch.watched.cityId : candidate.city_id
  // CR-01: never offer to create a venue the form's town already lists under
  // this name. A second row would make matchVenueByName ambiguous (it needs
  // exactly one), so every later candidate would fall back to custom. The
  // history keeps counting after a save (D-06 does not relink), so this is
  // also what stops a repeat offer once the new venue is in the list.
  // P-11 (33.4): a name the town lists under an alias is a listed twin too.
  // Only the whole text counts (D-05 step 1, code review WR-B02): a new name
  // of which one comma/dash part is a listed venue ("Igreja Matriz, Sines")
  // makes no existing key ambiguous, so it may still be saved.
  const listedTwin =
    listedVenuesNamed(venues, currentCityId, venueNameNow).length > 0 ||
    venueIdsForText(venueNameNow ?? '', currentCityId, venueIndex).ids.length > 0
  // UAT 2026-11-02: the town may list the same place under a longer or
  // shorter name ("Cine Granadeiro - Auditório Municipal" for "Cine
  // Granadeiro"). Offer those as "Use <venue>" instead of creating a copy.
  const similarListedVenues = onCustomVenue
    ? listedVenuesResembling(venues, currentCityId, venueNameNow)
        .slice(0, RESEMBLING_VENUES_MAX)
        .map((venue) => ({ id: venue.id, name: venue.name ?? '' }))
    : []
  // WR-08: only in a listed town. A venue saved with city_id null is never
  // matched (matchVenueByName needs a city_id on both sides), while the
  // history would keep offering it.
  const saveAsVenue =
    currentCityId !== null &&
    !listedTwin &&
    similarListedVenues.length === 0 &&
    activeMatch &&
    isSaveAsVenueEligible(activeMatch, venueNameNow)
      ? toVenueSuggestion(activeMatch, activePick.location)
      : null
  // CR-01: checked again inside the modal, right before the insert, on a fresh
  // read (another tab may have created the venue since this list loaded). The
  // fresh read lands in useQueryVenues' own cache entry, so the venue it finds
  // is in the form's list when the form selects it.
  const findListedVenue = async ({ name, cityId }: { name: string; cityId: number | null }) => {
    const fresh = await queryClient.fetchQuery({
      queryKey: ['venues', { name: undefined, page: undefined }],
      queryFn: () => queryVenues({}),
      staleTime: 0
    })
    const twin = listedVenuesNamed(fresh.venues, cityId, name)[0]
    return twin ? { id: twin.id, name: twin.name ?? '' } : null
  }
  const townCity = currentCityId === null ? undefined : cities?.find((city) => city.id === currentCityId)
  const mapFallbackCenter =
    townCity && townCity.lat !== null && townCity.lng !== null ? { lat: townCity.lat, lng: townCity.lng } : null

  const renderField = (field: EventFormFieldId, node: ReactNode) => (
    <DeskFieldFlag
      field={field}
      checks={flags[field] ?? []}
      variant='check'
      notes={notesFor(field)}
    >
      {node}
    </DeskFieldFlag>
  )

  // D-11 / Pitfall 9: re-read just before any event is created. A candidate
  // decided meanwhile stops here; the re-read also puts the decided state on
  // screen (the cache now holds the processed row).
  const beforeConvert = async (): Promise<boolean> => {
    let fresh: ApplicationRow
    try {
      fresh = await queryClient.fetchQuery({
        queryKey: ['desk', 'candidate', candidate.id],
        queryFn: () => queryDeskCandidate(candidate.id),
        staleTime: 0
      })
    } catch (failure) {
      showDeskError(t, failure)
      return false
    }
    if (!fresh.processed) return true
    showAlreadyDecided()
    return false
  }

  // DESK-07, D-09: the baseline is a pure transform of the row, the same one
  // the form opened with, compared with what she submitted.
  // An upload is read from the form's own signal (AD-CR-03): it never
  // changes values.picture.
  const markProcessed = async (
    applicationId: number,
    values: EventFormValues,
    extra: { weekly: boolean; uploaded: boolean }
  ) => {
    const baseline = buildEditorBaseline(candidate, { ...prefillDeps, language, customVenueKeyword })
    const editedFields = computeEditedFields(baseline, toSubmitted(values, extra.weekly, extra.uploaded))
    await approve.mutateAsync({ id: applicationId, editedFields })
  }

  // D-16: the ticked Remember offer becomes one alias row, written only after
  // a successful approve (onApproved runs after the processed write). Its own
  // failure is its own red note: it never undoes the approval and never
  // reaches onApproveError. 'already' (23505) is silent.
  // Code review WR-B01: the write finishes and the alias is put in the cache
  // BEFORE the desk moves on, so the next candidate (often a sibling post with
  // the same text) opens on the remembered venue; the refetch still follows.
  const rememberAlias = async (offer: RememberAliasOffer) => {
    try {
      await insertVenueAlias({ alias: offer.text, venue_id: offer.venueId })
      const sameAlias = (row: VenueAliasRow) =>
        row.venue_id === offer.venueId && row.alias.trim().toLowerCase() === offer.text.toLowerCase()
      queryClient.setQueryData<VenueAliasRow[]>([VENUE_ALIASES_QUERY_KEY], (rows) =>
        rows === undefined || rows.some(sameAlias) ? rows : [...rows, { alias: offer.text, venue_id: offer.venueId }]
      )
    } catch {
      notifications.show({ color: 'red', message: t('desk.venue_location.remember_failed') })
    } finally {
      void queryClient.invalidateQueries({ queryKey: [VENUE_ALIASES_QUERY_KEY] })
    }
  }

  const onApproved = async () => {
    // Captured before goToNext, which moves to another candidate.
    const remember = rememberWriteRef.current
    notifications.show({ color: 'green', message: t('desk.candidate.approved') })
    if (remember) await rememberAlias(remember)
    void goToNext(candidate.id)
  }

  const onApproveError = (failure: unknown) => {
    showDeskError(t, failure)
    if (mapDeskError(failure).alreadyDecided) {
      void queryClient.invalidateQueries({ queryKey: ['desk', 'candidate', candidate.id] })
    }
  }

  const deskMode: EventFormDeskMode = {
    mode: 'approve',
    submitLabel: t('desk.approve'),
    backLabel,
    onBack: () => navigate(backHref),
    disableAiMatch: true,
    markProcessed,
    beforeConvert,
    onApproved,
    onApproveError,
    renderField,
    pictureOverride: swapped?.id === candidate.id ? swapped.url : null,
    translationFill: translation.fill,
    onWatchedChange: townWatch.onWatchedChange,
    onVenueNameChange,
    onVenueChange,
    // D-24: the badge while the editor's model venue is still selected;
    // D-14 / D-15: the Remember box while the offer holds.
    venueAssist: {
      hint: showSuggestedVenueHint(candidate, selectedVenueId) ? t('desk.venue_location.suggested_venue') : null,
      remember: rememberOffer
        ? {
            label: t('desk.venue_location.remember_alias', { text: rememberOffer.text, name: rememberOffer.venueName }),
            checked: rememberChecked,
            onChange: (checked: boolean) => setRememberCheckedKey(checked ? rememberKey : null)
          }
        : null
    },
    locationFill,
    mapFallbackCenter,
    venueLocationNote,
    saveAsVenue,
    similarListedVenues,
    findListedVenue
  }

  return shell(
    <>
      {topBar(true)}
      <Grid gutter={16}>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Box style={{ position: 'sticky', top: 16 }}>
            <Stack gap={16}>
              <ChecksPanel row={checkRow} recheck={line} onRecheck={recheck.retry} cities={cities ?? []} />
              <SourcePostPanel rawItem={rawItem} application={candidate}>
                <ImageStrip
                  key={candidate.id}
                  application={candidate}
                  rawItem={rawItem}
                  onSwapped={(url) => setSwapped({ id: candidate.id, url })}
                />
              </SourcePostPanel>
            </Stack>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 8 }}>
          {isUpdateProposal(candidate) ? (
            <UpdateProposalPanel
              key={candidate.id}
              application={candidate}
              onDecided={() => void goToNext(candidate.id, 'updates')}
            />
          ) : (
          <Stack gap={16}>
            {hasStatusChange && (
              <Alert
                id={DESK_STATUS_BANNER_ID}
                color='orange'
                icon={<IconAlertTriangle size={18} />}
                title={t('desk.status_change.title')}
              >
                <Text size='sm'>{t('desk.status_change.body')}</Text>
              </Alert>
            )}
            {/* Keyed per candidate, but never the bare id: EventForm below is a
                sibling keyed by it, and the re-check (33.1) can mount this panel
                on an open candidate, where a shared key left a stale copy. */}
            {showsMatchPanel(candidate) && (
              <MatchPanel
                key={`match-${candidate.id}`}
                application={candidate}
                onDecided={() => void goToNext(candidate.id)}
                recheckPending={holdMatch}
                formRow={matchFormRow}
              />
            )}
            {/* D-07: inline, not a toast, so it stays while she works; never the raw error. Approve is never held (D-04). */}
            {translation.pending && (
              <Group gap={8}>
                <Loader size='xs' />
                <Text size='sm' c='dimmed'>{t('desk.translation.pending')}</Text>
              </Group>
            )}
            {translation.failed && (
              <Alert
                color='orange'
                icon={<IconAlertTriangle size={18} />}
                title={t('desk.translation.failed_title')}
              >
                <Text size='sm'>{t('desk.translation.failed_body')}</Text>
              </Alert>
            )}
            <EventForm
              key={candidate.id}
              isNew={false}
              event={prefill}
              venues={venues}
              organizers={organizers}
              tags={tags}
              currOrganizerId={undefined}
              organizerVenues={[]}
              isApplicationEdit={true}
              applicationContext={{
                id: candidate.id,
                source: candidate.source,
                sourceUrl: candidate.source_url,
                sourceUid: candidate.source_uid
              }}
              weekly={deskWeekly(candidate)}
              desk={deskMode}
            />
          </Stack>
          )}
        </Grid.Col>
      </Grid>
    </>
  )
}
